import { usePage, router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ArrowLeft, RotateCcw, Shield, UserX } from "lucide-react"

interface DisabledUser {
  id: string
  user_id: string
  email: string
  name: string | null
  role: "regular" | "admin"
  stage_ids: string[]
  disabled_at: string
  created_at: string
}

interface PageProps {
  auth: {
    user: { id: string; email: string; name: string | null }
    accounts: Array<{ id: string; name: string; slug: string }>
    currentAccountId: string | null
    role: string | null
  }
  flash: {
    notice: string | null
    alert: string | null
  }
  users: DisabledUser[]
  [key: string]: unknown
}

export default function UsersDisabled() {
  const { t } = useTranslation()
  const { auth, flash, users } = usePage<PageProps>().props

  function handleReactivate(user: DisabledUser) {
    if (confirm(t("users.confirmReactivate"))) {
      router.delete(`/users/${user.id}/deactivation`)
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      {flash.notice && (
        <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-md">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-md">
          {flash.alert}
        </div>
      )}

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("users.disabledTitle")}</h2>
            <p className="text-muted-foreground">{t("users.disabledSubtitle")}</p>
          </div>
          <Button variant="outline" onClick={() => router.get("/users")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("users.backToUsers")}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="w-5 h-5" />
              {t("users.disabledTitle")}
            </CardTitle>
            <CardDescription>{t("users.disabledSubtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t("users.noDisabledUsers")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("users.nameLabel")}</TableHead>
                    <TableHead>{t("users.emailLabel")}</TableHead>
                    <TableHead>{t("users.roleLabel")}</TableHead>
                    <TableHead>{t("users.disabledAt")}</TableHead>
                    <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow key={user.id} className={index % 2 === 1 ? "bg-muted" : ""}>
                      <TableCell className="font-medium">{user.name || t("users.noName")}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          {user.role === "admin" ? t("users.roleAdmin") : t("users.roleRegular")}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(user.disabled_at)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReactivate(user)}
                          title={t("users.reactivate")}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          {t("users.reactivate")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
